/**
 * Clinic landing content — Admin-only: edit before/after and landing media for any clinic.
 * Lists clinics from GET /api/v1/partners/medical/clinics.
 * Edit modal: BeforeAfterEditor + intro / testimonial URLs.
 * Save via PUT /api/v1/partners/medical/clinics/:id.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
  Icon,
  ReadableCell,
  Row,
  TooltipComponent,
} from "../../../components/Component";
import { Alert, FormGroup, Modal, ModalBody } from "reactstrap";
import moment from "moment";
import swal from "sweetalert";
import http from "../../../utils/http-common";
import { getPublicUrl } from "../../../utils/Utils";
import BeforeAfterEditor from "../../../components/partials/user-manage/BeforeAfterEditor";
import { Link } from "react-router-dom";

const getBaseUrl = () => {
  if (typeof window !== "undefined" && window.location?.hostname) {
    if (window.location.hostname === "admin.inoqare.com" || window.location.hostname === "www.admin.inoqare.com") {
      return "https://www.backend.inoqare.com";
    }
  }
  return process.env.REACT_APP_API_URL || "http://localhost:5000";
};

const getClinicPhotoUrl = (photoPath) => {
  if (!photoPath || !String(photoPath).trim()) return "https://via.placeholder.com/120?text=Before%2FAfter";
  const path = String(photoPath).trim();
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const base = getBaseUrl();
  return path.startsWith("/") ? `${base}${path}` : `${base}/${path}`;
};

const MyClinics = () => {
  const [clinics, setClinics] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editClinic, setEditClinic] = useState(null);
  const [saving, setSaving] = useState(false);
  const token = useMemo(() => localStorage.getItem("accessToken"), []);

  const fetchClinics = useCallback(() => {
    if (!token) {
      setClinics([]);
      setInitialLoading(false);
      return;
    }
    setInitialLoading(true);
    setError("");
    http
      .get("/api/v1/partners/medical/clinics")
      .then((res) => {
        const list = res.data?.data || res.data?.clinics || res.data || [];
        setClinics(Array.isArray(list) ? list : []);
      })
      .catch((e) => {
        const msg = e.response?.data?.message || e.message || "Failed to load clinics";
        setError(msg);
        setClinics([]);
        swal("Error", msg, "error");
      })
      .finally(() => setInitialLoading(false));
  }, [token]);

  useEffect(() => {
    fetchClinics();
  }, [fetchClinics]);

  const fetchClinicById = useCallback(
    (id) => {
      return http.get(`/api/v1/partners/medical/clinics/${id}`).then((res) => {
        const raw = res.data?.data || res.data;
        if (!raw) throw new Error("Clinic not found");
        const rawCases = Array.isArray(raw.beforeAfterCases) ? raw.beforeAfterCases : [];
        const beforeAfterCases = rawCases.map((c, i) => ({
          title: String(c?.title ?? "").trim(),
          imageUrl: String(c?.imageUrl ?? "").trim(),
          order: typeof c?.order === "number" && !Number.isNaN(c.order) && c.order >= 0 ? Math.floor(c.order) : i,
        }));
        return {
          ...raw,
          introVideoUrl: raw?.introVideoUrl || "",
          testimonialVideos: Array.isArray(raw?.testimonialVideos)
            ? raw.testimonialVideos.map((v) => ({ title: v?.title || "", url: v?.url || "", thumbnail: v?.thumbnail || null }))
            : [],
          beforeAfterCases,
        };
      });
    },
    []
  );

  const onEditClick = (clinic) => {
    const id = clinic?._id || clinic?.id;
    if (!id) {
      swal("Error", "Clinic ID missing", "error");
      return;
    }
    setEditClinic(null);
    setModalOpen(true);
    fetchClinicById(id)
      .then((data) => setEditClinic(data))
      .catch((e) => {
        swal("Error", e.response?.data?.message || e.message || "Failed to load clinic", "error");
        setModalOpen(false);
      });
  };

  const onSave = () => {
    if (!editClinic) return;
    const id = editClinic._id || editClinic.id;
    if (!id) {
      swal("Error", "Clinic ID missing", "error");
      return;
    }
    setSaving(true);
    const payload = {
      beforeAfterCases: (Array.isArray(editClinic.beforeAfterCases) ? editClinic.beforeAfterCases : []).map((c, i) => ({
        title: String(c?.title ?? "").trim(),
        imageUrl: String(c?.imageUrl ?? "").trim(),
        order: typeof c?.order === "number" && !Number.isNaN(c.order) && c.order >= 0 ? Math.floor(c.order) : i,
      })),
      introVideoUrl: editClinic.introVideoUrl ? String(editClinic.introVideoUrl).trim() || null : null,
      testimonialVideos: (Array.isArray(editClinic.testimonialVideos) ? editClinic.testimonialVideos : [])
        .filter((v) => v && v.url && String(v.url).trim())
        .map((v) => ({
          title: String(v.title || "").trim(),
          url: String(v.url).trim(),
          thumbnail: v.thumbnail ? String(v.thumbnail).trim() : null,
        })),
    };
    http
      .put(`/api/v1/partners/medical/clinics/${id}`, { ...editClinic, ...payload })
      .then((res) => {
        if (res.data?.success !== false) {
          swal("Success", "Clinic updated successfully", "success");
          fetchClinics();
          setModalOpen(false);
          setEditClinic(null);
        } else {
          throw new Error(res.data?.message || "Update failed");
        }
      })
      .catch((e) => {
        swal("Error", e.response?.data?.message || e.message || "Update failed", "error");
      })
      .finally(() => setSaving(false));
  };

  const closeModal = () => {
    if (!saving) {
      setModalOpen(false);
      setEditClinic(null);
    }
  };

  return (
    <React.Fragment>
      <Head title="Clinic landing content" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Clinic landing content
              </BlockTitle>
              <BlockDes className="text-soft">
                {initialLoading ? (
                  <span className="d-flex align-items-center">
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Loading...
                  </span>
                ) : (
                  <>
                    Admin: {clinics.length} clinic{clinics.length !== 1 ? "s" : ""} — quick edit for intro URL, testimonials, and before/after.{" "}
                    <Link to={getPublicUrl("media-library")} className="text-primary">
                      Media library
                    </Link>{" "}
                    for files.
                    {error && (
                      <span className="text-danger ms-2">
                        <Icon name="alert-circle" className="me-1" />
                        {error}
                      </span>
                    )}
                  </>
                )}
              </BlockDes>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Alert color="info" className="mt-3">
          <strong>Two admin paths, same API.</strong> For the full <em>Landing media</em> experience (banner hero, drag-and-drop uploads, choose from
          library, copy from another clinic, photos block), open{" "}
          <Link to={getPublicUrl("clinics")} className="alert-link">
            Clinics
          </Link>{" "}
          and edit the clinic there. This page uses a lighter form (URLs + before/after editor) over the same{" "}
          <code>introVideoUrl</code>, <code>testimonialVideos</code>, and <code>beforeAfterCases</code> fields.
        </Alert>

        <Block>
          <DataTable className="card-stretch" scrollable>
            <DataTableBody>
              <DataTableHead>
                <DataTableRow size="lg">
                  <span className="sub-text">Clinic</span>
                </DataTableRow>
                <DataTableRow size="md">
                  <span className="sub-text">Address</span>
                </DataTableRow>
                <DataTableRow size="md">
                  <span className="sub-text">Status</span>
                </DataTableRow>
                <DataTableRow size="md">
                  <span className="sub-text">Created</span>
                </DataTableRow>
                <DataTableRow className="nk-tb-col-tools text-right">
                  <span className="sub-text">Actions</span>
                </DataTableRow>
              </DataTableHead>

              {initialLoading ? (
                <DataTableItem>
                  <DataTableRow colSpan={5} className="text-center py-5">
                    <span className="spinner-border text-primary" role="status" aria-hidden="true" />
                    <p className="mt-2 text-soft">Loading clinics...</p>
                  </DataTableRow>
                </DataTableItem>
              ) : Array.isArray(clinics) && clinics.length > 0 ? (
                clinics.map((c, index) => (
                  <DataTableItem key={c?._id || c?.id || `clinic-${index}`}>
                    <DataTableRow size="lg" dataLabel="Clinic">
                      <ReadableCell>{c?.name || "—"}</ReadableCell>
                      {c?.isDefault && <div className="small text-soft">Default</div>}
                    </DataTableRow>
                    <DataTableRow size="md" dataLabel="Address">
                      <ReadableCell>
                        {[c?.address?.address, c?.address?.street, c?.address?.city, c?.address?.country].filter(Boolean).join(", ") || "—"}
                      </ReadableCell>
                    </DataTableRow>
                    <DataTableRow size="md" dataLabel="Status">
                      <span>{c?.status || "—"}</span>
                    </DataTableRow>
                    <DataTableRow size="md" dataLabel="Created">
                      <span>{c?.createdAt ? moment(c.createdAt).format("DD/MM/YY") : "—"}</span>
                    </DataTableRow>
                    <DataTableRow className="nk-tb-col-tools text-right" dataLabel="Actions">
                      <TooltipComponent
                        id={`edit-my-${c?._id || index}`}
                        tag="button"
                        type="button"
                        containerClassName="btn btn-trigger btn-icon"
                        icon="edit-alt-fill"
                        direction="top"
                        text="Edit clinic (before/after &amp; landing)"
                        onClick={(e) => {
                          e.preventDefault();
                          onEditClick(c);
                        }}
                        aria-label="Edit clinic"
                      />
                    </DataTableRow>
                  </DataTableItem>
                ))
              ) : (
                <DataTableItem>
                  <DataTableRow colSpan={5} className="text-center py-5">
                    <span className="text-silent">No clinics found. Check API access or partner data.</span>
                  </DataTableRow>
                </DataTableItem>
              )}
            </DataTableBody>
          </DataTable>
        </Block>

        <Modal isOpen={modalOpen} toggle={closeModal} className="modal-dialog-centered" size="lg">
          <ModalBody>
            <button
              type="button"
              className="close"
              onClick={closeModal}
              disabled={saving}
              aria-label="Close"
            >
              <Icon name="cross-sm" aria-hidden="true" />
            </button>
            <div className="p-2">
              <h5 className="title">Edit clinic — Before/After &amp; landing content</h5>
              {!editClinic ? (
                <div className="text-center py-4">
                  <span className="spinner-border text-primary" role="status" aria-hidden="true" />
                  <p className="mt-2 text-soft">Loading clinic...</p>
                </div>
              ) : (
                <div className="mt-4">
                  <p className="text-soft small mb-3">
                    <strong>{editClinic.name}</strong> — Changes apply to the clinic landing and Real results section.
                  </p>

                  <Row>
                    <Col md="12">
                      <h6 className="overline-title">Landing content (optional)</h6>
                    </Col>
                    <Col md="12">
                      <FormGroup>
                        <label className="form-label">Intro video URL</label>
                        <input
                          type="url"
                          className="form-control"
                          placeholder="https://... or /videos/..."
                          value={editClinic.introVideoUrl || ""}
                          onChange={(e) => setEditClinic((prev) => ({ ...prev, introVideoUrl: e.target.value }))}
                          aria-label="Intro video URL"
                        />
                        <small className="text-soft">Hero video on the landing page.</small>
                      </FormGroup>
                    </Col>
                    <Col md="12">
                      <FormGroup>
                        <label className="form-label">Testimonial videos</label>
                        {(Array.isArray(editClinic.testimonialVideos) ? editClinic.testimonialVideos : []).map((item, idx) => (
                          <div key={idx} className="d-flex flex-g-2 align-items-start mb-2">
                            <input
                              type="text"
                              className="form-control flex-grow-1"
                              placeholder="Title (e.g. Patient 1)"
                              value={item.title || ""}
                              onChange={(e) =>
                                setEditClinic((prev) => {
                                  const arr = [...(prev.testimonialVideos || [])];
                                  arr[idx] = { ...arr[idx], title: e.target.value };
                                  return { ...prev, testimonialVideos: arr };
                                })
                              }
                              aria-label={`Testimonial ${idx + 1} title`}
                            />
                            <input
                              type="url"
                              className="form-control flex-grow-1"
                              placeholder="URL (YouTube or direct video)"
                              value={item.url || ""}
                              onChange={(e) =>
                                setEditClinic((prev) => {
                                  const arr = [...(prev.testimonialVideos || [])];
                                  arr[idx] = { ...arr[idx], url: e.target.value };
                                  return { ...prev, testimonialVideos: arr };
                                })
                              }
                              aria-label={`Testimonial ${idx + 1} URL`}
                            />
                            <Button
                              color="danger"
                              size="sm"
                              outline
                              type="button"
                              onClick={() =>
                                setEditClinic((prev) => ({
                                  ...prev,
                                  testimonialVideos: (prev.testimonialVideos || []).filter((_, i) => i !== idx),
                                }))
                              }
                              aria-label={`Remove testimonial ${idx + 1}`}
                            >
                              <Icon name="trash" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          color="light"
                          size="sm"
                          type="button"
                          onClick={() =>
                            setEditClinic((prev) => ({
                              ...prev,
                              testimonialVideos: [...(prev.testimonialVideos || []), { title: "", url: "", thumbnail: null }],
                            }))
                          }
                        >
                          <Icon name="plus" className="me-1" />
                          Add testimonial video
                        </Button>
                      </FormGroup>
                    </Col>

                    <Col md="12">
                      <h6 className="overline-title">Before/After (Real results)</h6>
                      <p className="text-soft small mb-2">
                        Upload images in the{" "}
                        <Link to={getPublicUrl("media-library")}>Media library</Link>
                        , then paste the image URL in the field below.
                      </p>
                      <BeforeAfterEditor
                        cases={editClinic.beforeAfterCases ?? []}
                        onChange={(next) => setEditClinic((prev) => ({ ...prev, beforeAfterCases: next }))}
                        getImageUrl={getClinicPhotoUrl}
                        disabled={saving}
                      />
                    </Col>

                    <Col md="12" className="mt-4">
                      <Button color="primary" onClick={onSave} disabled={saving} type="button">
                        {saving ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                            Saving...
                          </>
                        ) : (
                          "Save changes"
                        )}
                      </Button>
                      <Button color="light" className="ms-2" onClick={closeModal} disabled={saving} type="button">
                        Cancel
                      </Button>
                    </Col>
                  </Row>
                </div>
              )}
            </div>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default MyClinics;
